import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import Icon from 'components/Icon';
import {
	MINIMO_CARACTERES,
	buscarMunicipio,
	buscarMunicipios,
} from 'services/municipios';
import { textoDoErro } from 'services/erros';

/**
 * Campo único de cidade, com busca nacional por digitação.
 *
 * Substitui o par de <select> Estado + Cidade: a UF deixa de ser entrada e passa
 * a sair junto do município escolhido. Vive em components/ e não em
 * components/Profissional/ porque atende mais de uma tela.
 *
 * Sempre controlado: `value` é o código IBGE (string). O texto digitado é estado
 * interno e nunca é o valor — só Enter e clique confirmam uma escolha, o que torna
 * impossível terminar com texto na tela e valor nenhum no formulário.
 */
export default function SeletorCidade({
	id: idExterno,
	value = '',
	rotuloInicial = '',
	onChange,
	onBlur,
	label = 'Cidade',
	rotuloVisivel = true,
	// Os formulários usam o .label do design system; a tela de busca escreve os
	// rótulos em versalete miúdo, para casarem com "Cobrança por" e "Ordenar por"
	// que ficam na mesma linha. Um prop resolve sem duplicar o componente.
	classeRotulo = 'label',
	obrigatorio = false,
	erro = '',
	disabled = false,
	autoFocus = false,
	className = '',
	placeholder = 'Digite o nome da cidade',
	permitirLimpar = true,
	ufPreferida,
	limite = 8,
}) {
	const idGerado = useId();
	const id = idExterno || idGerado;
	const idLista = `${id}-lista`;
	const idErro = `${id}-erro`;

	const raizRef = useRef(null);
	const inputRef = useRef(null);
	const listaRef = useRef(null);

	// `rotulo` é o texto do município confirmado; `texto` é o que está digitado.
	// Fora de foco, os dois coincidem — é o que faz Tab e Esc reverterem sozinhos.
	const [rotulo, setRotulo] = useState(rotuloInicial);
	const [texto, setTexto] = useState(rotuloInicial);
	const [aberto, setAberto] = useState(false);
	const [indiceAtivo, setIndiceAtivo] = useState(-1);
	const [resultado, setResultado] = useState({ itens: [], total: 0 });
	const [falhouCarga, setFalhouCarga] = useState(false);

	const focado = useRef(false);
	const codigo = value ? String(value) : '';

	/**
	 * Hidrata o rótulo a partir do código.
	 *
	 * O valor costuma chegar sozinho — de um cookie, do JWT ou do banco — sem o
	 * nome junto. `rotuloInicial` pinta o campo no primeiro quadro com o que quem
	 * chama já tem em mãos, e isto aqui troca pelo rótulo autoritativo assim que o
	 * dataset resolve, sem nenhuma ida à rede no caminho comum.
	 */
	useEffect(() => {
		if (!codigo) {
			setRotulo('');
			if (!focado.current) setTexto('');
			return;
		}

		let vivo = true;
		buscarMunicipio(codigo)
			.then((municipio) => {
				if (!vivo || !municipio) return;
				setRotulo(municipio.rotulo);
				if (!focado.current) setTexto(municipio.rotulo);
			})
			.catch(() => {
				if (vivo) setFalhouCarga(true);
			});

		return () => {
			vivo = false;
		};
	}, [codigo]);

	// Rótulo vindo de fora (um reset() do react-hook-form, por exemplo) com o
	// código ainda igual: sem isto, o campo ficaria mostrando o valor antigo.
	useEffect(() => {
		if (!rotuloInicial) return;
		setRotulo((atual) => atual || rotuloInicial);
		if (!focado.current) setTexto((atual) => atual || rotuloInicial);
	}, [rotuloInicial]);

	const ufDesempate = useMemo(() => {
		if (ufPreferida) return ufPreferida;
		const partes = rotulo.split(' - ');
		return partes.length === 2 ? partes[1] : '';
	}, [ufPreferida, rotulo]);

	// Busca a cada tecla, sem debounce: é filtro local sobre 5.571 itens, na casa
	// do décimo de milissegundo. Debounce aqui só adicionaria latência percebida.
	useEffect(() => {
		if (!aberto) return;

		let vivo = true;
		buscarMunicipios(texto, { limite, ufPreferida: ufDesempate })
			.then((r) => {
				if (!vivo) return;
				setResultado(r);
				setIndiceAtivo(r.itens.length > 0 ? 0 : -1);
			})
			.catch(() => {
				if (!vivo) return;
				setFalhouCarga(true);
				setResultado({ itens: [], total: 0 });
			});

		return () => {
			vivo = false;
		};
	}, [texto, aberto, limite, ufDesempate]);

	// Fechar ao clicar fora. O onBlur do React sozinho não serve: ele dispara antes
	// do clique na opção e cancelaria a própria escolha. Mesmo padrão do dropdown
	// de período em Agenda.jsx, o único do projeto que já nascia com isto certo.
	useEffect(() => {
		if (!aberto) return;

		const aoClicarFora = (evento) => {
			if (raizRef.current && !raizRef.current.contains(evento.target)) {
				setAberto(false);
				setTexto(rotulo);
			}
		};

		document.addEventListener('mousedown', aoClicarFora);
		return () => document.removeEventListener('mousedown', aoClicarFora);
	}, [aberto, rotulo]);

	// Mantém a opção ativa à vista sem rolar a página junto.
	useEffect(() => {
		if (!aberto || indiceAtivo < 0 || !listaRef.current) return;
		const item = listaRef.current.children[indiceAtivo];
		if (item && item.scrollIntoView) item.scrollIntoView({ block: 'nearest' });
	}, [indiceAtivo, aberto]);

	const confirmar = useCallback(
		(municipio) => {
			setRotulo(municipio.rotulo);
			setTexto(municipio.rotulo);
			setAberto(false);
			setIndiceAtivo(-1);
			if (onChange) onChange(municipio);
		},
		[onChange]
	);

	const limpar = useCallback(() => {
		setRotulo('');
		setTexto('');
		setAberto(false);
		setIndiceAtivo(-1);
		if (onChange) onChange(null);
		if (inputRef.current) inputRef.current.focus();
	}, [onChange]);

	const aoTeclar = (evento) => {
		const { itens } = resultado;

		if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
			evento.preventDefault();
			if (!aberto) {
				setAberto(true);
				return;
			}
			if (itens.length === 0) return;
			const passo = evento.key === 'ArrowDown' ? 1 : -1;
			setIndiceAtivo((atual) => (atual + passo + itens.length) % itens.length);
			return;
		}

		if (evento.key === 'Enter') {
			// preventDefault incondicional com a lista aberta: sem isto, dentro dos
			// <form> das telas de cadastro o Enter que escolhe a cidade também
			// submeteria o formulário inteiro.
			if (!aberto) return;
			evento.preventDefault();

			const escolhido = itens[indiceAtivo] || (itens.length === 1 ? itens[0] : null);
			if (escolhido) confirmar(escolhido);
			return;
		}

		if (evento.key === 'Escape') {
			if (aberto) {
				setAberto(false);
			} else {
				setTexto(rotulo);
			}
			return;
		}

		if (evento.key === 'Tab' && aberto) {
			setAberto(false);
			setTexto(rotulo);
		}
	};

	const aoSair = (evento) => {
		focado.current = false;
		// Sair do campo NÃO confirma a opção ativa. A regra única — só Enter e
		// clique confirmam — é o que garante que ninguém termine com um nome
		// digitado na tela e nenhuma cidade no formulário.
		setAberto(false);
		setTexto(rotulo);
		if (onBlur) onBlur(evento);
	};

	const temResultados = resultado.itens.length > 0;
	const curto = texto.trim().length < MINIMO_CARACTERES;
	const mostrarPainel = aberto && !falhouCarga;

	const anuncio = (() => {
		if (!mostrarPainel) return '';
		if (curto) return '';
		if (!temResultados) return 'Nenhuma cidade encontrada.';
		if (resultado.total > resultado.itens.length) {
			return `${resultado.total} cidades encontradas, mostrando ${resultado.itens.length}.`;
		}
		return `${resultado.total} ${resultado.total === 1 ? 'cidade encontrada' : 'cidades encontradas'}.`;
	})();

	return (
		<div ref={raizRef} className={`relative ${className}`}>
			<label
				htmlFor={id}
				className={rotuloVisivel ? classeRotulo : 'sr-only'}
			>
				{label} {obrigatorio && rotuloVisivel && <span className="text-error">*</span>}
			</label>

			<div className="relative">
				<input
					id={id}
					ref={inputRef}
					type="text"
					role="combobox"
					aria-expanded={mostrarPainel}
					aria-controls={idLista}
					aria-autocomplete="list"
					aria-activedescendant={
						mostrarPainel && indiceAtivo >= 0 ? `${id}-opcao-${indiceAtivo}` : undefined
					}
					aria-invalid={!!erro}
					aria-describedby={erro ? idErro : undefined}
					aria-required={obrigatorio || undefined}
					autoComplete="off"
					autoFocus={autoFocus}
					disabled={disabled}
					placeholder={placeholder}
					value={texto}
					onChange={(evento) => {
						setTexto(evento.target.value);
						setAberto(true);
					}}
					onFocus={() => {
						focado.current = true;
						setAberto(true);
					}}
					onBlur={aoSair}
					onKeyDown={aoTeclar}
					className={`input pr-10 ${erro ? 'input-error' : ''}`}
				/>

				{permitirLimpar && !!texto && !disabled ? (
					<button
						type="button"
						onClick={limpar}
						aria-label="Limpar cidade"
						className="btn-icone absolute right-2 top-1/2 -translate-y-1/2 p-1.5"
					>
						<Icon name="close" className="h-4 w-4" />
					</button>
				) : (
					<Icon
						name="search"
						className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
					/>
				)}
			</div>

			<span role="status" aria-live="polite" className="sr-only">
				{anuncio}
			</span>

			{erro && (
				<p id={idErro} role="alert" className="text-error text-sm mt-1">
					{erro}
				</p>
			)}

			{falhouCarga && (
				// Falha de carga não pode esvaziar o campo: o rótulo e o valor atuais
				// seguem na tela, então uma busca já em andamento continua funcionando.
				<p role="alert" className="text-error text-sm mt-1">
					{textoDoErro('LOC-101')}
				</p>
			)}

			{/* Dropdown ancorado em todas as larguras, e não bottom-sheet como o
			    OverlayCalendario: campo de digitação abre teclado, e um painel preso
			    ao rodapé nasceria atrás dele. O teto de altura evita medir viewport em
			    JS, que o index.css já resolve por media query em outros lugares.
			    z-20 de propósito: passa por baixo da NavBar, que é z-30. */}
			{mostrarPainel && (
				<div className="animate-slide-down absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-xl border-caixa border-border bg-surface shadow-lg">
					{curto ? (
						<p className="px-4 py-3 text-sm text-muted">
							Digite ao menos {MINIMO_CARACTERES} letras do nome da cidade.
						</p>
					) : !temResultados ? (
						<p className="px-4 py-3 text-sm text-muted">
							Nenhuma cidade encontrada para “{texto.trim()}”.
						</p>
					) : (
						<>
							<ul
								id={idLista}
								ref={listaRef}
								role="listbox"
								aria-label="Cidades encontradas"
								className="max-h-[min(45vh,20rem)] overflow-y-auto"
							>
								{resultado.itens.map((municipio, indice) => {
									const ativo = indice === indiceAtivo;
									return (
										<li
											key={municipio.codigo}
											id={`${id}-opcao-${indice}`}
											role="option"
											aria-selected={ativo}
											// Confirmar no mousedown, e não no click: o clique
											// chegaria depois do blur, que já teria fechado a lista.
											onMouseDown={(evento) => {
												evento.preventDefault();
												confirmar(municipio);
											}}
											onMouseEnter={() => setIndiceAtivo(indice)}
											className={`cursor-pointer px-4 py-2.5 text-base ${
												ativo ? 'bg-primary/10 text-dark' : 'text-dark'
											}`}
										>
											<span className="font-medium">{municipio.nome}</span>
											<span className="text-muted"> - {municipio.uf}</span>
										</li>
									);
								})}
							</ul>

							{resultado.total > resultado.itens.length && (
								<p className="border-t border-border px-4 py-2 text-xs text-muted">
									{resultado.total} cidades encontradas. Continue digitando para
									refinar.
								</p>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
}
